import React, { useState, useEffect } from 'react';
import Header from './header';
import Footer from './footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSort, faSortUp, faSortDown, faPlus } from '@fortawesome/free-solid-svg-icons';

const Watchlist = () => {
    const [watchlist, setWatchlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [deleteConfirm, setDeleteConfirm] = useState({ show: false, item: null });
    const [currentPage, setCurrentPage] = useState(1);
    const [showAddModal, setShowAddModal] = useState(false);
    const [activeTab, setActiveTab] = useState('search');
    const [overwriteWatchlist, setOverwriteWatchlist] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedSearchItems, setSelectedSearchItems] = useState([]);
    const [csvFile, setCsvFile] = useState(null);
    const [pasteText, setPasteText] = useState('');
    const [modalError, setModalError] = useState('');
    const [modalLoading, setModalLoading] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: '' });
    const itemsPerPage = 10;

    useEffect(() => {
        fetchWatchlist();
    }, []);

    const fetchWatchlist = async () => {        
        try {
            setLoading(true);
            setError('');

            // Get userId and auth_token from sessionStorage
            const userIdRaw = sessionStorage.getItem('userId');
            const authTokenRaw = sessionStorage.getItem('token');
            
            console.log('Raw values - userId:', userIdRaw, 'token:', authTokenRaw);
            
            // Parse only if not null/undefined
            const userId = userIdRaw && userIdRaw !== 'undefined' ? JSON.parse(userIdRaw) : null;
            const auth_token = authTokenRaw && authTokenRaw !== 'undefined' ? JSON.parse(authTokenRaw) : null;
            
            console.log('Retrieved from sessionStorage - userId:', userId, 'auth_token:', auth_token);

            if (!userId || !auth_token) {
                setError('User information not found. Please login again.');
                setLoading(false);
                return;
            }

            // Construct the API URL with userId and token as query params
            const apiUrl = `/api/watchlist?userId=${userId}&token=${auth_token}`;
            
            // Log the API call details
            console.log('=== Watchlist API Call ===');
            console.log('URL:', apiUrl);
            console.log('Full URL:', window.location.origin + apiUrl);
            console.log('Method: GET');
            console.log('userId:', userId);
            console.log('auth_token:', auth_token);
            console.log('========================');

            // Fetch watchlist
            const response = await fetch(apiUrl, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch watchlist: ${response.statusText}`);
            }

            const data = await response.json();
            console.log('Watchlist API Response:', data);
            console.log('Watchlist Data:', data.data);
            if (data.data && data.data.length > 0) {
                console.log('Sample Item:', data.data[0]);
            }
            setWatchlist(data.data || data || []);
        } catch (err) {
            console.error('Error fetching watchlist:', err);
            setError(err.message || 'Failed to load watchlist');
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getSortedData = () => {
        if (!sortConfig.key) return watchlist;

        const sorted = [...watchlist].sort((a, b) => {
            const aValue = a[sortConfig.key] || '';
            const bValue = b[sortConfig.key] || '';

            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <FontAwesomeIcon icon={faSort} style={{ opacity: 0.3, marginLeft: '5px' }} />;
        }
        return sortConfig.direction === 'asc' 
            ? <FontAwesomeIcon icon={faSortUp} style={{ marginLeft: '5px' }} />
            : <FontAwesomeIcon icon={faSortDown} style={{ marginLeft: '5px' }} />;
    };

    const handleCheckboxChange = (ticker) => {
        setSelectedTickers(prev => 
            prev.includes(ticker) 
                ? prev.filter(t => t !== ticker)
                : [...prev, ticker]
        );
    };

    const handleDelete = (item) => {
        setDeleteConfirm({ show: true, item });
    };

    const confirmDelete = async () => {
        const item = deleteConfirm.item;
        
        try {
            const userId = JSON.parse(sessionStorage.getItem('userId'));
            const auth_token = JSON.parse(sessionStorage.getItem('token'));
            
            const response = await fetch('/api/watchlist/remove', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                    token: auth_token,
                    tickers: item.subject || item.ticker
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to remove item: ${response.statusText}`);
            }

            // Remove from local state
            setWatchlist(prev => prev.filter(i => i !== item));
            setDeleteConfirm({ show: false, item: null });
            
            // Show success toast
            setToast({ show: true, message: `${item.subject || item.ticker} removed successfully`, type: 'success' });
            setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
            
        } catch (err) {
            console.error('Error deleting item:', err);
            setDeleteConfirm({ show: false, item: null });
            
            // Show error toast
            setToast({ show: true, message: err.message || 'Failed to delete item', type: 'error' });
            setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
        }
    };

    const cancelDelete = () => {
        setDeleteConfirm({ show: false, item: null });
    };

    const handleRemoveSelected = async () => {
        if (selectedTickers.length === 0) {
            setModalError('Please select at least one item to remove.');
            return;
        }

        if (!window.confirm(`Are you sure you want to remove ${selectedTickers.length} selected item(s)?`)) {
            return;
        }

        try {
            const userId = JSON.parse(sessionStorage.getItem('userId'));
            const auth_token = JSON.parse(sessionStorage.getItem('token'));
            
            const response = await fetch('/api/watchlist/remove', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId,
                    token: auth_token,
                    tickers: selectedTickers.join(',')
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to remove items: ${response.statusText}`);
            }

            // Remove from local state
            setWatchlist(prev => prev.filter(item => !selectedTickers.includes(item.subject || item.ticker)));
            const count = selectedTickers.length;
            setSelectedTickers([]);
            setCurrentPage(1); // Reset to first page
            
            // Show success toast
            setToast({ show: true, message: `${count} item(s) removed successfully`, type: 'success' });
            setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
            
        } catch (err) {
            setToast({ show: true, message: err.message || 'Failed to remove items', type: 'error' });
            setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
        }
    };

    const handleSearchChange = async (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.length < 2) {
            setSearchResults([]);
            return;
        }

        try {
            const userId = JSON.parse(sessionStorage.getItem('userId'));
            const auth_token = JSON.parse(sessionStorage.getItem('token'));
            
            const response = await fetch(`/api/watchlist/search?q=${encodeURIComponent(query)}&userId=${userId}&token=${auth_token}`);
            if (response.ok) {
                const data = await response.json();
                const results = data.data || data.results || [];
                
                // Filter out tickers that are already in the watchlist
                const existingTickers = watchlist.map(item => (item.subject || item.ticker).toUpperCase());
                const filteredResults = results.filter(item => 
                    !existingTickers.includes(item.ticker.toUpperCase())
                );
                
                setSearchResults(filteredResults);
            }
        } catch (err) {
            console.error('Search error:', err);
        }
    };

    const addSearchItem = (item) => {
        if (!selectedSearchItems.find(i => i.ticker === item.ticker)) {
            setSelectedSearchItems([...selectedSearchItems, item]);
        }
        setSearchQuery('');
        setSearchResults([]);
    };

    const removeSearchItem = (ticker) => {
        setSelectedSearchItems(selectedSearchItems.filter(i => i.ticker !== ticker));
    };

    const getScoreColor = (score) => {
        const numScore = parseFloat(score);
        if (isNaN(numScore)) return '#fff';
        
        if (numScore >= 3) {
            return '#0da705';
        } else if (numScore >= 2 && numScore < 3) {
            return '#77d062';
        } else if (numScore >= 1 && numScore < 2) {
            return '#26c500';
        } else if (numScore >= -1 && numScore <= 1) {
            return '#cecbcb';
        } else if (numScore < -1 && numScore > -2) {
            return '#9f0004';
        } else if (numScore <= -2 && numScore > -3) {
            return '#9f0004';
        } else if (numScore <= -3) {
            return '#9f0004';
        }
        return '#fff';
    };

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file && file.name.endsWith('.txt')) {
            setCsvFile(file);
        } else {
            setModalError('Please upload a .txt file');
        }
    };

    const handleAddToWatchlist = async () => {
        setModalLoading(true);
        setModalError('');

        try {
            const userId = JSON.parse(sessionStorage.getItem('userId'));
            const auth_token = JSON.parse(sessionStorage.getItem('token'));
            
            let tickers = [];

            if (activeTab === 'search') {
                tickers = selectedSearchItems.map(i => i.ticker);
            } else if (activeTab === 'upload' && csvFile) {
                const text = await csvFile.text();
                tickers = text.split(/[\n,]/).map(t => t.trim()).filter(t => t);
            } else if (activeTab === 'paste') {
                tickers = pasteText.split(/[\n,]/).map(t => t.trim()).filter(t => t);
            }

            if (tickers.length === 0) {
                setModalError('Please add at least one ticker');
                setModalLoading(false);
                return;
            }

            const response = await fetch('/api/watchlist/add', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    userId: userId,
                    token: auth_token,
                    tickers: tickers.join(','),
                    ticker_limit: 100,
                    alerts: false,
                    overwrite_existing: overwriteWatchlist
                })
            });

            if (!response.ok) {
                throw new Error(`Failed to add to watchlist: ${response.statusText}`);
            }

            // Refresh watchlist
            await fetchWatchlist();
            
            const count = tickers.length;
            
            // Reset modal state
            setShowAddModal(false);
            setSelectedSearchItems([]);
            setCsvFile(null);
            setPasteText('');
            setOverwriteWatchlist(false);
            setActiveTab('search');
            
            // Show success toast
            setToast({ show: true, message: `${count} ticker(s) added successfully`, type: 'success' });
            setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
            
        } catch (err) {
            setModalError(err.message || 'Failed to add to watchlist');
        } finally {
            setModalLoading(false);
        }
    };

    const sortedWatchlist = getSortedData();

    // Pagination logic
    const totalPages = Math.ceil(sortedWatchlist.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const paginatedWatchlist = sortedWatchlist.slice(startIndex, endIndex);

    const goToPage = (page) => {
        setCurrentPage(page);
    };

    const goToPrevious = () => {
        if (currentPage > 1) setCurrentPage(currentPage - 1);
    };

    const goToNext = () => {
        if (currentPage < totalPages) setCurrentPage(currentPage + 1);
    };

    return (
        <div className="wrapper">
            <Header />
            <div className="content" style={{ padding: '20px', minHeight: '80vh', backgroundColor: '#191717' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h1 style={{ color: '#fff', margin: 0 }}>My Watchlist</h1>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={handleRemoveSelected}
                            disabled={selectedTickers.length === 0}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: selectedTickers.length === 0 ? '#444' : '#ff4444',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: selectedTickers.length === 0 ? 'not-allowed' : 'pointer',
                                fontSize: '14px',
                                fontWeight: '500',
                                opacity: selectedTickers.length === 0 ? 0.5 : 1
                            }}
                        >
                            <FontAwesomeIcon icon={faTrash} style={{ marginRight: '8px' }} />
                            Remove Selected ({selectedTickers.length})
                        </button>
                        <button
                            onClick={() => setShowAddModal(true)}
                            style={{
                                padding: '10px 20px',
                                backgroundColor: '#2b97f0',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '5px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: '500'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#1e7ac7'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#2b97f0'}
                        >
                            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '8px' }} />
                            Add
                        </button>
                    </div>
                </div>

                {loading && (
                    <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>
                        Loading watchlist...
                    </div>
                )}

                {error && (
                    <div style={{
                        color: 'white',
                        backgroundColor: 'red',
                        padding: '15px',
                        borderRadius: '5px',
                        marginBottom: '20px'
                    }}>
                        {error}
                    </div>
                )}

                {!loading && !error && watchlist.length === 0 && (
                    <div style={{ color: '#fff', textAlign: 'center', padding: '40px' }}>
                        No items in your watchlist yet.
                    </div>
                )}

                {!loading && !error && watchlist.length > 0 && (
                    <div style={{ overflowX: 'auto' }}>
                        <table style={{
                            width: '100%',
                            borderCollapse: 'collapse',
                            backgroundColor: '#2a2a2a',
                            color: '#fff'
                        }}>
                            <thead>
                                <tr style={{ backgroundColor: '#111525', borderBottom: '2px solid #444', fontSize: '13px' }}>
                                    <th style={{ padding: '12px', textAlign: 'left', width: '50px' }}>
                                        <input 
                                            type="checkbox" 
                                            onChange={(e) => {
                                                if (e.target.checked) {
                                                    setSelectedTickers(watchlist.map(item => item.subject || item.ticker));
                                                } else {
                                                    setSelectedTickers([]);
                                                }
                                            }}
                                            checked={selectedTickers.length === watchlist.length && watchlist.length > 0}
                                        />
                                    </th>
                                    <th 
                                        style={{ padding: '12px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleSort('subject')}
                                    >
                                        Subject {getSortIcon('subject')}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleSort('companyname')}
                                    >
                                        Company {getSortIcon('companyname')}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleSort('sector')}
                                    >
                                        Sector {getSortIcon('sector')}
                                    </th>
                                    <th 
                                        style={{ padding: '12px', textAlign: 'left', cursor: 'pointer', whiteSpace: 'nowrap' }}
                                        onClick={() => handleSort('industry')}
                                    >
                                        Industry {getSortIcon('industry')}
                                    </th>
                                    <th style={{ padding: '12px', textAlign: 'center', width: '80px' }}>
                                        Action
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedWatchlist.map((item, index) => (
                                    <tr 
                                        key={item.id || index}
                                        style={{
                                            borderBottom: '1px solid #444',
                                            transition: 'background-color 0.2s'
                                        }}
                                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#333'}
                                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                                    >
                                        <td style={{ padding: '12px', textAlign: 'left' }}>
                                            <input 
                                                id={`tickers[${item.subject || item.ticker}]`}
                                                type="checkbox" 
                                                name={`tickers[${item.subject || item.ticker}]`}
                                                checked={selectedTickers.includes(item.subject || item.ticker)}
                                                onChange={() => handleCheckboxChange(item.subject || item.ticker)}
                                            />
                                        </td>
                                        <td style={{ padding: '12px', color: '#2b97f0', fontWeight: 'bold' }}>
                                            {item.subject || item.ticker || '-'}
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            {item.companyname || item.name || '-'}
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            {item.sector || '-'}
                                        </td>
                                        <td style={{ padding: '12px' }}>
                                            {item.industry || '-'}
                                        </td>
                                        <td style={{ padding: '12px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => handleDelete(item)}
                                                style={{
                                                    background: 'transparent',
                                                    border: 'none',
                                                    color: '#ff4444',
                                                    cursor: 'pointer',
                                                    fontSize: '16px',
                                                    padding: '5px 10px'
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#ff0000'}
                                                onMouseLeave={(e) => e.target.style.color = '#ff4444'}
                                            >
                                                <FontAwesomeIcon icon={faTrash} />
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div style={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                marginTop: '20px',
                                gap: '10px'
                            }}>
                                <button
                                    onClick={goToPrevious}
                                    disabled={currentPage === 1}
                                    style={{
                                        padding: '8px 15px',
                                        backgroundColor: currentPage === 1 ? '#444' : '#2b97f0',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        opacity: currentPage === 1 ? 0.5 : 1
                                    }}
                                >
                                    Previous
                                </button>

                                <span style={{ color: '#ccc', fontSize: '14px' }}>
                                    Page {currentPage} of {totalPages} ({sortedWatchlist.length} items)
                                </span>

                                <button
                                    onClick={goToNext}
                                    disabled={currentPage === totalPages}
                                    style={{
                                        padding: '8px 15px',
                                        backgroundColor: currentPage === totalPages ? '#444' : '#2b97f0',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        opacity: currentPage === totalPages ? 0.5 : 1
                                    }}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Custom Delete Confirmation Modal */}
                {deleteConfirm.show && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#2a2a2a',
                            padding: '30px',
                            borderRadius: '8px',
                            maxWidth: '400px',
                            width: '90%',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                            border: '1px solid #444'
                        }}>
                            <h3 style={{ color: '#fff', marginTop: 0, marginBottom: '15px' }}>
                                Confirm Delete
                            </h3>
                            <p style={{ color: '#ccc', marginBottom: '25px' }}>
                                Are you sure you want to delete <strong style={{ color: '#2b97f0' }}>
                                    {deleteConfirm.item?.subject || deleteConfirm.item?.ticker || deleteConfirm.item?.companyname}
                                </strong> from your watchlist?
                            </p>
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={cancelDelete}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#444',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#555'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#444'}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={confirmDelete}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#ff4444',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#ff0000'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#ff4444'}
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Add to Watchlist Modal */}
                {showAddModal && (
                    <div style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 1000
                    }}>
                        <div style={{
                            backgroundColor: '#2a2a2a',
                            padding: '30px',
                            borderRadius: '8px',
                            maxWidth: '600px',
                            width: '90%',
                            maxHeight: '80vh',
                            overflow: 'auto',
                            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                            border: '1px solid #444'
                        }}>
                            <h3 style={{ color: '#fff', marginTop: 0, marginBottom: '20px' }}>
                                Add to Watchlist
                            </h3>

                            {/* Tabs */}
                            <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '1px solid #444' }}>
                                <button
                                    onClick={() => setActiveTab('search')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'transparent',
                                        color: activeTab === 'search' ? '#2b97f0' : '#ccc',
                                        border: 'none',
                                        borderBottom: activeTab === 'search' ? '2px solid #2b97f0' : 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Search
                                </button>
                                <button
                                    onClick={() => setActiveTab('upload')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'transparent',
                                        color: activeTab === 'upload' ? '#2b97f0' : '#ccc',
                                        border: 'none',
                                        borderBottom: activeTab === 'upload' ? '2px solid #2b97f0' : 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Upload CSV
                                </button>
                                <button
                                    onClick={() => setActiveTab('paste')}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: 'transparent',
                                        color: activeTab === 'paste' ? '#2b97f0' : '#ccc',
                                        border: 'none',
                                        borderBottom: activeTab === 'paste' ? '2px solid #2b97f0' : 'none',
                                        cursor: 'pointer',
                                        fontSize: '14px'
                                    }}
                                >
                                    Paste
                                </button>
                            </div>

                            {/* Tab Content */}
                            <div style={{ minHeight: '300px' }}>
                            {activeTab === 'search' && (
                                <div>
                                    <input
                                        type="text"
                                        placeholder="Search for tickers..."
                                        value={searchQuery}
                                        onChange={handleSearchChange}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#333',
                                            color: '#fff',
                                            border: '1px solid #555',
                                            borderRadius: '5px',
                                            fontSize: '14px',
                                            marginBottom: '10px'
                                        }}
                                    />
                                    
                                    {searchResults.length > 0 && (
                                        <div style={{
                                            backgroundColor: '#333',
                                            border: '1px solid #555',
                                            borderRadius: '5px',
                                            maxHeight: '150px',
                                            overflowY: 'auto',
                                            marginBottom: '10px'
                                        }}>
                                            {searchResults.map((item, idx) => (
                                                <div
                                                    key={idx}
                                                    onClick={() => addSearchItem(item)}
                                                    style={{
                                                        padding: '10px',
                                                        cursor: 'pointer',
                                                        borderBottom: '1px solid #444',
                                                        color: '#fff'
                                                    }}
                                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#444'}
                                                    onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                                >
                                                    <strong>{item.ticker}</strong> - {item.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedSearchItems.length > 0 && (
                                        <div style={{ marginTop: '15px' }}>
                                            <label style={{ color: '#ccc', fontSize: '14px', marginBottom: '10px', display: 'block' }}>
                                                Selected Tickers:
                                            </label>
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                                {selectedSearchItems.map((item) => (
                                                    <div
                                                        key={item.ticker}
                                                        style={{
                                                            backgroundColor: '#2b97f0',
                                                            color: '#fff',
                                                            padding: '5px 10px',
                                                            borderRadius: '15px',
                                                            fontSize: '13px',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: '8px'
                                                        }}
                                                    >
                                                        {item.ticker}
                                                        <span
                                                            onClick={() => removeSearchItem(item.ticker)}
                                                            style={{ cursor: 'pointer', fontWeight: 'bold' }}
                                                        >
                                                            ×
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'upload' && (
                                <div>
                                    <p style={{ color: '#ccc', fontSize: '14px', marginBottom: '15px' }}>
                                        Upload a .txt file with tickers (one per line or comma-separated)
                                    </p>
                                    <input
                                        type="file"
                                        accept=".txt"
                                        onChange={handleFileUpload}
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#333',
                                            color: '#fff',
                                            border: '1px solid #555',
                                            borderRadius: '5px',
                                            fontSize: '14px'
                                        }}
                                    />
                                    {csvFile && (
                                        <p style={{ color: '#2b97f0', fontSize: '13px', marginTop: '10px' }}>
                                            Selected: {csvFile.name}
                                        </p>
                                    )}
                                </div>
                            )}

                            {activeTab === 'paste' && (
                                <div>
                                    <p style={{ color: '#ccc', fontSize: '14px', marginBottom: '15px' }}>
                                        Paste tickers (one per line or comma-separated)
                                    </p>
                                    <textarea
                                        placeholder="AAPL&#10;MSFT&#10;GOOGL"
                                        value={pasteText}
                                        onChange={(e) => setPasteText(e.target.value)}
                                        rows="8"
                                        style={{
                                            width: '100%',
                                            padding: '10px',
                                            backgroundColor: '#333',
                                            color: '#fff',
                                            border: '1px solid #555',
                                            borderRadius: '5px',
                                            fontSize: '14px',
                                            fontFamily: 'monospace',
                                            resize: 'vertical'
                                        }}
                                    />
                                </div>
                            )}
                            </div>

                            {modalError && (
                                <div style={{
                                    color: '#ff4444',
                                    backgroundColor: 'rgba(255, 68, 68, 0.1)',
                                    padding: '10px',
                                    borderRadius: '5px',
                                    marginTop: '15px',
                                    fontSize: '14px'
                                }}>
                                    {modalError}
                                </div>
                            )}

                            {/* Overwrite Checkbox */}
                            <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                                <label style={{ color: '#ccc', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={overwriteWatchlist}
                                        onChange={(e) => setOverwriteWatchlist(e.target.checked)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                    Overwrite Existing Watchlist
                                </label>
                            </div>

                            {/* Action Buttons */}
                            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={() => {
                                        setShowAddModal(false);
                                        setModalError('');
                                        setSelectedSearchItems([]);
                                        setCsvFile(null);
                                        setPasteText('');
                                        setSearchQuery('');
                                        setSearchResults([]);
                                    }}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: '#444',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => e.target.style.backgroundColor = '#555'}
                                    onMouseLeave={(e) => e.target.style.backgroundColor = '#444'}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleAddToWatchlist}
                                    disabled={modalLoading}
                                    style={{
                                        padding: '10px 20px',
                                        backgroundColor: modalLoading ? '#444' : '#2b97f0',
                                        color: '#fff',
                                        border: 'none',
                                        borderRadius: '5px',
                                        cursor: modalLoading ? 'not-allowed' : 'pointer',
                                        fontSize: '14px',
                                        fontWeight: '500'
                                    }}
                                    onMouseEnter={(e) => !modalLoading && (e.target.style.backgroundColor = '#1e7ac7')}
                                    onMouseLeave={(e) => !modalLoading && (e.target.style.backgroundColor = '#2b97f0')}
                                >
                                    {modalLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* Toast Notification */}
            {toast.show && (
                <div style={{
                    position: 'fixed',
                    top: '20px',
                    right: '20px',
                    backgroundColor: toast.type === 'success' ? '#4caf50' : '#f44336',
                    color: '#fff',
                    padding: '15px 25px',
                    borderRadius: '5px',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.3)',
                    zIndex: 10000,
                    fontSize: '14px',
                    fontWeight: '500',
                    animation: 'slideIn 0.3s ease-out'
                }}>
                    {toast.message}
                </div>
            )}

            <Footer />
        </div>
    );
};

export default Watchlist;
